import { useEffect } from 'react';
import type { MapEntityArea, LayerVisibility } from './types';
import { BUFFER_SIZE } from './types';

interface AreaCanvasInput {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    mapEntityAreas: MapEntityArea[];
    inProgressVertices: { x: number; y: number }[];
    cursorVertex: { x: number; y: number } | null;
    isDrawingArea: boolean;
    dimensions: { width: number; height: number };
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    selectedWorldUid: string | null;
    hoveredWorldUid: string | null;
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    cameraTick: number;
    layerVisibility: LayerVisibility;
    activeSection: string;
    resolveEntityName: (ref: any) => string;
}

/**
 * Side-effect hook: draws world areas and the in-progress polygon
 * onto the dedicated area canvas.  Extracted from map-canvas.tsx to
 * keep the main component focused on orchestration only.
 */
export function useAreaCanvas({
    canvasRef,
    mapEntityAreas,
    inProgressVertices,
    cursorVertex,
    isDrawingArea,
    dimensions,
    zoomRef,
    panRef,
    selectedWorldUid,
    hoveredWorldUid,
    isCustomUpload,
    customMapImageRef,
    cameraTick,
    layerVisibility,
    activeSection,
    resolveEntityName,
}: AreaCanvasInput): void {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const { width, height } = dimensions;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);

        const isCustom = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom ? customMapImageRef!.current!.naturalWidth : BUFFER_SIZE;
        const mH = isCustom ? customMapImageRef!.current!.naturalHeight : BUFFER_SIZE;

        let rW = Math.min(width, height);
        let rH = Math.min(width, height);
        if (isCustom) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; }
            else { rW = width; rH = width / ratio; }
        }

        const zoom = zoomRef.current;
        const pan = panRef.current;

        const toScreen = (x: number, y: number) => ({
            sx: (x / mW) * rW * zoom + pan.x,
            sy: (y / mH) * rH * zoom + pan.y,
        });

        const visibleAreas = (activeSection === 'world' || layerVisibility.areas) ? mapEntityAreas : [];

        // Draw placed areas
        for (const area of visibleAreas) {
            if (area.vertices.length < 2) continue;
            const { style, uid } = area;
            const isSelected = selectedWorldUid === uid;
            const isHovered = hoveredWorldUid === uid;

            ctx.beginPath();
            const first = toScreen(area.vertices[0].x, area.vertices[0].y);
            ctx.moveTo(first.sx, first.sy);
            for (let i = 1; i < area.vertices.length; i++) {
                const { sx, sy } = toScreen(area.vertices[i].x, area.vertices[i].y);
                ctx.lineTo(sx, sy);
            }
            ctx.closePath();

            ctx.fillStyle = style.fillColor;
            ctx.globalAlpha = isHovered || isSelected
                ? Math.min(style.fillOpacity * 2, 0.6)
                : style.fillOpacity;
            ctx.fill();
            ctx.globalAlpha = 1;

            ctx.strokeStyle = isSelected ? '#ffffff' : style.borderColor;
            ctx.lineWidth = isSelected ? style.borderWidth + 1.5 : style.borderWidth;
            ctx.setLineDash(style.dashPattern);
            ctx.stroke();
            ctx.setLineDash([]);

            if (isSelected) {
                ctx.fillStyle = '#ffffff';
                for (const v of area.vertices) {
                    const { sx, sy } = toScreen(v.x, v.y);
                    ctx.beginPath();
                    ctx.arc(sx, sy, 5, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.strokeStyle = style.borderColor;
                    ctx.lineWidth = 1.5;
                    ctx.stroke();
                }
            }

            if (area.vertices.length >= 3) {
                const cx = area.vertices.reduce((s, v) => s + v.x, 0) / area.vertices.length;
                const cy = area.vertices.reduce((s, v) => s + v.y, 0) / area.vertices.length;
                const { sx, sy } = toScreen(cx, cy);
                ctx.font = `bold ${Math.max(10, 12 * zoom)}px sans-serif`;
                ctx.fillStyle = style.borderColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.globalAlpha = 0.9;
                ctx.fillText(resolveEntityName(area.entityRef), sx, sy);
                ctx.globalAlpha = 1;
            }
        }

        // Draw in-progress polygon
        if (isDrawingArea && inProgressVertices.length > 0) {
            ctx.beginPath();
            const fp = toScreen(inProgressVertices[0].x, inProgressVertices[0].y);
            ctx.moveTo(fp.sx, fp.sy);
            for (let i = 1; i < inProgressVertices.length; i++) {
                const { sx, sy } = toScreen(inProgressVertices[i].x, inProgressVertices[i].y);
                ctx.lineTo(sx, sy);
            }
            if (cursorVertex) {
                const { sx, sy } = toScreen(cursorVertex.x, cursorVertex.y);
                ctx.lineTo(sx, sy);
            }
            ctx.strokeStyle = '#f59e0b';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 3]);
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.fillStyle = '#f59e0b';
            for (const v of inProgressVertices) {
                const { sx, sy } = toScreen(v.x, v.y);
                ctx.beginPath();
                ctx.arc(sx, sy, 5, 0, Math.PI * 2);
                ctx.fill();
            }

            // Snap hint: ring around first vertex when cursor is close
            if (inProgressVertices.length >= 3 && cursorVertex) {
                const { sx: fsx, sy: fsy } = toScreen(inProgressVertices[0].x, inProgressVertices[0].y);
                const { sx: csx, sy: csy } = toScreen(cursorVertex.x, cursorVertex.y);
                if (Math.hypot(fsx - csx, fsy - csy) < 20) {
                    ctx.beginPath();
                    ctx.arc(fsx, fsy, 10, 0, Math.PI * 2);
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }
            }
        }
    }, [
        canvasRef, mapEntityAreas, inProgressVertices, cursorVertex, isDrawingArea,
        dimensions, zoomRef, panRef, selectedWorldUid, hoveredWorldUid,
        isCustomUpload, customMapImageRef, cameraTick, layerVisibility, activeSection, resolveEntityName,
    ]);
}
